import axios, { AxiosInstance, AxiosError } from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Base URL per le immagini e file statici (rimuove /api dalla fine)
const BASE_URL = API_URL.replace(/\/api$/, '');

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor per aggiungere il token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor per gestire errori
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Token scaduto o non valido
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  async login(email: string, password: string) {
    const response = await this.api.post('/auth/login', { email, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  }

  async register(userData: any) {
    const response = await this.api.post('/auth/register', userData);
    return response.data;
  }

  async getAuthConfig() {
    const response = await this.api.get('/auth/config');
    return response.data;
  }

  async getCurrentUser() {
    const response = await this.api.get('/auth/me');
    return response.data;
  }

  // Tenant selection (multi-tenant)
  async selectTenant(userId: number, tenantId: number, selectionToken: string) {
    const response = await this.api.post('/auth/select-tenant', {
      userId,
      tenantId,
      selectionToken
    });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  }

  // Switch tenant (per utenti con piu tenant)
  async switchTenant(tenantId: number) {
    const response = await this.api.post('/auth/switch-tenant', { tenantId });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  }

  // Get user's tenants
  async getUserTenants() {
    const response = await this.api.get('/tenants/me/tenants');
    return response.data;
  }

  // Tenant management (Platform Admin)
  async getTenants() {
    const response = await this.api.get('/tenants');
    return response.data.tenants || [];
  }

  async getTenant(id: number) {
    const response = await this.api.get(`/tenants/${id}`);
    return response.data;
  }

  async createTenant(data: any) {
    const response = await this.api.post('/tenants', data);
    return response.data;
  }

  async updateTenant(id: number, data: any) {
    const response = await this.api.put(`/tenants/${id}`, data);
    return response.data;
  }

  async uploadTenantLogo(tenantId: number, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await this.api.post(`/tenants/${tenantId}/upload-logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getTenantUsers(tenantId: number) {
    const response = await this.api.get(`/tenants/${tenantId}/users`);
    return response.data.users || [];
  }

  async addUserToTenant(tenantId: number, data: { userId: number; role: string; isDefault?: boolean }) {
    const response = await this.api.post(`/tenants/${tenantId}/users`, data);
    return response.data;
  }

  async updateTenantUser(tenantId: number, userId: number, data: { role: string }) {
    const response = await this.api.put(`/tenants/${tenantId}/users/${userId}`, data);
    return response.data;
  }

  async removeTenantUser(tenantId: number, userId: number) {
    const response = await this.api.delete(`/tenants/${tenantId}/users/${userId}`);
    return response.data;
  }

  async unlockUser(tenantId: number, userId: number): Promise<{
    message: string;
    email: string;
    wasLocked: boolean;
    previousAttempts: number;
  }> {
    const response = await this.api.post(`/tenants/${tenantId}/users/${userId}/unlock`);
    return response.data;
  }

  async getUserLockoutStatus(tenantId: number, userId: number): Promise<{
    isLocked: boolean;
    failedAttempts: number;
    lockedUntil: string | null;
    lastFailedLogin: string | null;
  }> {
    const response = await this.api.get(`/tenants/${tenantId}/users/${userId}/lockout-status`);
    return response.data;
  }

  async searchUsers(search: string) {
    const response = await this.api.get('/users', { params: { search, limit: 20 } });
    // API returns { data: [...], total, stats, ... }
    return response.data.data || response.data.users || [];
  }

  // Clients
  async getClients(params?: any) {
    const response = await this.api.get('/clients', { params });
    return response.data;
  }

  async getClient(id: number) {
    const response = await this.api.get(`/clients/${id}`);
    return response.data;
  }

  async createClient(data: any) {
    const response = await this.api.post('/clients', data);
    return response.data;
  }

  async updateClient(id: number, data: any) {
    const response = await this.api.put(`/clients/${id}`, data);
    return response.data;
  }

  async deleteClient(id: number) {
    const response = await this.api.delete(`/clients/${id}`);
    return response.data;
  }

  // Export clienti
  async exportClients(format: 'excel' | 'csv' | 'json' | 'pdf') {
    const response = await this.api.get(`/clients/export/${format}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Import clienti
  async importClients(file: File, format: 'excel' | 'csv' | 'json') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('format', format);
    const response = await this.api.post('/clients/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // Products
  async getProducts(params?: any) {
    const response = await this.api.get('/products', { params });
    return response.data;
  }

  async getProduct(id: number) {
    const response = await this.api.get(`/products/${id}`);
    return response.data;
  }

  async createProduct(data: any) {
    const response = await this.api.post('/products', data);
    return response.data;
  }

  async updateProduct(id: number, data: any) {
    const response = await this.api.put(`/products/${id}`, data);
    return response.data;
  }

  async deleteProduct(id: number) {
    const response = await this.api.delete(`/products/${id}`);
    return response.data;
  }

  async duplicateProduct(id: number) {
    const response = await this.api.post(`/products/${id}/duplicate`);
    return response.data;
  }

  async exportProducts(format: 'excel' | 'csv' | 'json' | 'pdf' = 'excel') {
    const response = await this.api.get(`/products/export/${format}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async downloadProductTemplate(): Promise<Blob> {
    const response = await this.api.get('/products/template', {
      responseType: 'blob',
    });
    return response.data;
  }

  async importProducts(file: File, format: 'excel' | 'csv' | 'json') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('format', format);
    const response = await this.api.post('/products/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async uploadProductImage(productId: number, file: File) {
    const formData = new FormData();
    formData.append('image', file);
    const response = await this.api.post(`/products/${productId}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async uploadProductBrochure(productId: number, file: File) {
    const formData = new FormData();
    formData.append('brochure', file);
    const response = await this.api.post(`/products/${productId}/brochure`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteProductBrochure(productId: number) {
    const response = await this.api.delete(`/products/${productId}/brochure`);
    return response.data;
  }

  getBrochureUrl(path: string | null): string | null {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const baseUrl = this.api.defaults.baseURL?.replace('/api', '') || '';
    return `${baseUrl}${path}`;
  }

  // Product Relations
  async getProductRelations(productId: number) {
    const response = await this.api.get(`/products/${productId}/relations`);
    return response.data;
  }

  async addProductRelation(productId: number, relatedProductId: number, relationType: string = 'accessory', note?: string) {
    const response = await this.api.post(`/products/${productId}/relations`, {
      related_product_id: relatedProductId,
      relation_type: relationType,
      note,
    });
    return response.data;
  }

  async updateProductRelation(productId: number, relationId: number, relationType: string, note?: string) {
    const response = await this.api.put(`/products/${productId}/relations/${relationId}`, {
      relation_type: relationType,
      note,
    });
    return response.data;
  }

  async removeProductRelation(productId: number, relationId: number) {
    const response = await this.api.delete(`/products/${productId}/relations/${relationId}`);
    return response.data;
  }

  // Quotes
  async getQuotes(params?: any) {
    const response = await this.api.get('/quotes', { params });
    return response.data;
  }

  async getQuote(id: number) {
    const response = await this.api.get(`/quotes/${id}`);
    return response.data;
  }

  async createQuote(data: any) {
    const response = await this.api.post('/quotes', data);
    return response.data;
  }

  async updateQuote(id: number, data: any) {
    const response = await this.api.put(`/quotes/${id}`, data);
    return response.data;
  }

  async deleteQuote(id: number) {
    const response = await this.api.delete(`/quotes/${id}`);
    return response.data;
  }

  async updateQuoteStatus(id: number, status: string) {
    const response = await this.api.patch(`/quotes/${id}/status`, { status });
    return response.data;
  }

  async cancelQuote(id: number, cancellationReason?: string) {
    const response = await this.api.patch(`/quotes/${id}/cancel`, {
      cancellation_reason: cancellationReason
    });
    return response.data;
  }

  async approveQuote(id: number, notes?: string, hasModifications?: boolean): Promise<{ message: string; quote: any }> {
    const response = await this.api.patch(`/quotes/${id}/approve`, { notes, hasModifications });
    return response.data;
  }

  async rejectQuoteApproval(id: number, reason?: string): Promise<{ message: string; quote: any }> {
    const response = await this.api.patch(`/quotes/${id}/reject-approval`, { reason });
    return response.data;
  }

  async generateQuotePDF(id: number) {
    const response = await this.api.get(`/quotes/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  getQuotePdfUrl(id: number) {
    // Restituisce l'URL per aprire il PDF in nuova tab
    return `${this.api.defaults.baseURL}/quotes/${id}/pdf`;
  }

  async downloadQuotePdf(id: number, quoteNumber: string) {
    const response = await this.api.get(`/quotes/${id}/pdf`, {
      responseType: 'blob'
    });

    // Crea download automatico
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `preventivo-${quoteNumber}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // Dashboard
  async getDashboardStats() {
    const response = await this.api.get('/dashboard/stats');
    return response.data;
  }

  async getChartData(period?: string) {
    const response = await this.api.get('/dashboard/charts', {
      params: { period },
    });
    return response.data;
  }

  // Settings
  async getSettings(category?: string) {
    const response = await this.api.get('/settings', {
      params: { category },
    });
    return response.data;
  }

  async getSetting(key: string) {
    const response = await this.api.get(`/settings/${key}`);
    return response.data;
  }

  async updateSetting(key: string, value: any) {
    const response = await this.api.put(`/settings/${key}`, { value });
    return response.data;
  }

  async updateBulkSettings(settings: any) {
    const response = await this.api.post('/settings/bulk', settings);
    return response.data;
  }

  // Get bank details of current tenant
  async getBankDetails() {
    const response = await this.api.get('/settings/bank-details/current');
    return response.data;
  }

  async getGeneralTerms() {
    const response = await this.api.get('/settings/general-terms');
    return response.data as { general_terms_text: string };
  }

  async updateGeneralTerms(generalTermsText: string) {
    const response = await this.api.put('/settings/general-terms', { general_terms_text: generalTermsText });
    return response.data as { general_terms_text: string };
  }

  // Upload logo aziendale
  async uploadLogo(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('logo', file);

    const response = await this.api.post('/settings/upload-logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getVatRates() {
    const response = await this.api.get('/settings/vat-rates/list');
    return response.data;
  }

  async createVatRate(data: any) {
    const response = await this.api.post('/settings/vat-rates', data);
    return response.data;
  }

  async updateVatRate(id: number, data: any) {
    const response = await this.api.put(`/settings/vat-rates/${id}`, data);
    return response.data;
  }

  async deleteVatRate(id: number) {
    const response = await this.api.delete(`/settings/vat-rates/${id}`);
    return response.data;
  }

  // Metodo helper per ottenere URL completo delle immagini
  getImageUrl(imagePath: string | null | undefined): string | undefined {
    if (!imagePath) return undefined;
    const baseUrl = API_URL.replace(/\/api$/, '');
    return `${baseUrl}${imagePath}`;
  }

  // ==========================================
  // CATALOG API
  // ==========================================

  // Categorie
  async getCategories() {
    const response = await this.api.get('/catalog/categories');
    return response.data;
  }

  async getCategoriesFlat() {
    const response = await this.api.get('/catalog/categories/flat');
    return response.data;
  }

  // ==========================================
  // SUPPLIERS API
  // ==========================================

  async getSuppliers(search?: string) {
    const response = await this.api.get('/suppliers', { params: search ? { search } : {} });
    return response.data;
  }

  async getSupplier(id: number) {
    const response = await this.api.get(`/suppliers/${id}`);
    return response.data;
  }

  async createSupplier(data: any) {
    const response = await this.api.post('/suppliers', data);
    return response.data;
  }

  async updateSupplier(id: number, data: any) {
    const response = await this.api.put(`/suppliers/${id}`, data);
    return response.data;
  }

  async deleteSupplier(id: number) {
    const response = await this.api.delete(`/suppliers/${id}`);
    return response.data;
  }

  async createCategory(data: { name: string; code?: string; description?: string; parent_id?: number | null; icon?: string; color?: string; sort_order?: number }) {
    const response = await this.api.post('/catalog/categories', data);
    return response.data;
  }

  async updateCategory(id: number, data: { name: string; code?: string; description?: string; parent_id?: number | null; icon?: string; color?: string; sort_order?: number; is_active?: boolean }) {
    const response = await this.api.put(`/catalog/categories/${id}`, data);
    return response.data;
  }

  async deleteCategory(id: number) {
    const response = await this.api.delete(`/catalog/categories/${id}`);
    return response.data;
  }

  // Prodotti del catalogo
  async getCatalogProducts(params?: {
    search?: string;
    category_id?: number;
    product_type?: string;
    billing_model?: string;
    min_price?: number;
    max_price?: number;
    is_template?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const response = await this.api.get('/catalog/products', { params });
    return response.data;
  }

  async getCatalogProduct(id: number) {
    const response = await this.api.get(`/catalog/products/${id}`);
    return response.data;
  }

  async getProductsByCategory(categoryId: number, includeChildren = false) {
    const response = await this.api.get(`/catalog/products/by-category/${categoryId}`, {
      params: { include_children: includeChildren }
    });
    return response.data;
  }

  // Bundle
  async getBundles() {
    const response = await this.api.get('/catalog/bundles');
    return response.data;
  }

  async getBundle(id: number) {
    const response = await this.api.get(`/catalog/bundles/${id}`);
    return response.data;
  }

  async expandBundle(id: number, parentLineId: number | null = null) {
    const response = await this.api.post(`/catalog/bundles/${id}/expand`, {
      parent_line_id: parentLineId
    });
    return response.data;
  }

  // ============================================
  // BUNDLES MANAGEMENT API (con componenti)
  // ============================================

  async createBundleWithComponents(bundleData: any) {
    const response = await this.api.post('/bundles', bundleData);
    return response.data;
  }

  async getBundleWithComponents(id: number) {
    const response = await this.api.get(`/bundles/${id}`);
    return response.data;
  }

  async updateBundleWithComponents(id: number, bundleData: any) {
    const response = await this.api.put(`/bundles/${id}`, bundleData);
    return response.data;
  }

  async deleteBundleWithComponents(id: number) {
    const response = await this.api.delete(`/bundles/${id}`);
    return response.data;
  }

  // ============================================
  // OPTION GROUPS (Gruppi Opzioni)
  // ============================================

  // GET /api/option-groups - Lista gruppi opzioni
  async getOptionGroups(): Promise<any[]> {
    const response = await this.api.get('/option-groups');
    return response.data;
  }

  // GET /api/option-groups/:id - Dettaglio gruppo con prodotti
  async getOptionGroup(id: number): Promise<any> {
    const response = await this.api.get(`/option-groups/${id}`);
    return response.data;
  }

  // POST /api/option-groups - Crea nuovo gruppo
  async createOptionGroup(data: any): Promise<any> {
    const response = await this.api.post('/option-groups', data);
    return response.data;
  }

  // PUT /api/option-groups/:id - Aggiorna gruppo
  async updateOptionGroup(id: number, data: any): Promise<any> {
    const response = await this.api.put(`/option-groups/${id}`, data);
    return response.data;
  }

  // DELETE /api/option-groups/:id - Elimina gruppo
  async deleteOptionGroup(id: number): Promise<any> {
    const response = await this.api.delete(`/option-groups/${id}`);
    return response.data;
  }

  // ============================================
  // QUOTE TEMPLATES (Termini e Condizioni)
  // ============================================

  // GET /api/quote-templates - Lista tutti i template
  async getQuoteTemplates(): Promise<any[]> {
    const response = await this.api.get('/quote-templates');
    return response.data;
  }

  // GET /api/quote-templates/:categoryId - Template per categoria
  async getQuoteTemplate(categoryId: number): Promise<any> {
    const response = await this.api.get(`/quote-templates/${categoryId}`);
    return response.data;
  }

  // POST /api/quote-templates - Crea/Aggiorna template
  async saveQuoteTemplate(data: {
    category_id: number;
    template_name: string;
    template_text: string;
  }): Promise<any> {
    const response = await this.api.post('/quote-templates', data);
    return response.data;
  }

  // DELETE /api/quote-templates/:categoryId - Elimina template
  async deleteQuoteTemplate(categoryId: number): Promise<any> {
    const response = await this.api.delete(`/quote-templates/${categoryId}`);
    return response.data;
  }

  // GET /api/quote-templates/variables/all - Tutte le variabili
  async getAllTemplateVariables(): Promise<any[]> {
    const response = await this.api.get('/quote-templates/variables/all');
    return response.data;
  }

  // GET /api/quote-templates/variables/:categoryId - Variabili per categoria
  async getTemplateVariables(categoryId: number): Promise<any[]> {
    const response = await this.api.get(`/quote-templates/variables/${categoryId}`);
    return response.data;
  }

  // POST /api/quote-templates/variables - Crea/Aggiorna variabile
  async saveTemplateVariable(data: {
    category_id?: number;
    variable_name: string;
    variable_value: string;
    variable_type: string;
    variable_options?: string;
    is_global?: boolean;
    display_order?: number;
  }): Promise<any> {
    const response = await this.api.post('/quote-templates/variables', data);
    return response.data;
  }

  // DELETE /api/quote-templates/variables/:id - Elimina variabile
  async deleteTemplateVariable(id: number): Promise<any> {
    const response = await this.api.delete(`/quote-templates/variables/${id}`);
    return response.data;
  }

  // POST /api/quote-templates/render - Renderizza template con variabili
  async renderTemplate(data: {
    category_id: number;
    variables: Record<string, string>;
  }): Promise<{ renderedText: string }> {
    const response = await this.api.post('/quote-templates/render', data);
    return response.data;
  }

  // GET /api/quote-templates/quote-values/:quoteId - Valori salvati per preventivo
  async getQuoteTemplateValues(quoteId: number): Promise<any[]> {
    const response = await this.api.get(`/quote-templates/quote-values/${quoteId}`);
    return response.data;
  }

  // GET /api/quote-templates/quote-values-with-defaults/:quoteId/:categoryId
  async getQuoteTemplateValuesWithDefaults(quoteId: number, categoryId: number): Promise<any[]> {
    const response = await this.api.get(`/quote-templates/quote-values-with-defaults/${quoteId}/${categoryId}`);
    return response.data;
  }

  // POST /api/quote-templates/quote-values/:quoteId - Salva valori per preventivo
  async saveQuoteTemplateValues(quoteId: number, categoryId: number, variables: Record<string, string>): Promise<any> {
    const response = await this.api.post(`/quote-templates/quote-values/${quoteId}`, { 
      variables,
      category_id: categoryId 
    });
    return response.data;
  }

  // POST /api/quote-templates/render-for-quote/:quoteId - Renderizza template per preventivo
  async renderTemplateForQuote(quoteId: number): Promise<{ renderedText: string }> {
    const response = await this.api.post(`/quote-templates/render-for-quote/${quoteId}`);
    return response.data;
  }

  // GET /api/quote-templates/quote-categories/:quoteId - Categorie nel preventivo
  async getQuoteCategories(quoteId: number): Promise<any[]> {
    const response = await this.api.get(`/quote-templates/quote-categories/${quoteId}`);
    return response.data;
  }

  // ============================================
  // USERS MANAGEMENT API
  // ============================================

  // GET /api/users/roles - Lista ruoli disponibili
  async getUserRoles(): Promise<any[]> {
    const response = await this.api.get('/users/roles');
    return response.data;
  }

  // GET /api/users - Lista utenti
  async getUsers(params?: {
    search?: string;
    role?: string;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const response = await this.api.get('/users', { params });
    return response.data;
  }

  // GET /api/users/:id - Dettaglio utente
  async getUser(id: number): Promise<any> {
    const response = await this.api.get(`/users/${id}`);
    return response.data;
  }

  // POST /api/users - Crea utente (password auto-generata, email invito inviata)
  async createUser(data: {
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    is_active?: boolean;
    manager_id?: number | null;
    team_id?: number | null;
  }): Promise<any> {
    const response = await this.api.post('/users', data);
    return response.data;
  }

  // PUT /api/users/:id - Aggiorna utente
  async updateUser(id: number, data: {
    email?: string;
    first_name?: string;
    last_name?: string;
    role?: string;
    is_active?: boolean;
    manager_id?: number | null;
    team_id?: number | null;
  }): Promise<any> {
    const response = await this.api.put(`/users/${id}`, data);
    return response.data;
  }

  // GET /api/users/:id/deletion-impact - Pre-check impatto eliminazione
  async getUserDeletionImpact(id: number): Promise<any> {
    const response = await this.api.get(`/users/${id}/deletion-impact`);
    return response.data;
  }

  // PUT /api/users/:id/reassign-data - Riassegna dati utente
  async reassignUserData(fromUserId: number, toUserId: number): Promise<any> {
    const response = await this.api.put(`/users/${fromUserId}/reassign-data`, { toUserId });
    return response.data;
  }

  // DELETE /api/users/:id - Rimuovi utente dal tenant
  async deleteUser(id: number): Promise<any> {
    const response = await this.api.delete(`/users/${id}`);
    return response.data;
  }

  // PUT /api/users/:id/password - Cambia password
  async changeUserPassword(id: number, password: string): Promise<any> {
    const response = await this.api.put(`/users/${id}/password`, { password });
    return response.data;
  }

  // PUT /api/users/:id/toggle-active - Attiva/disattiva utente
  async toggleUserActive(id: number): Promise<any> {
    const response = await this.api.put(`/users/${id}/toggle-active`);
    return response.data;
  }

  // PUT /api/users/me/password - Cambio password utente corrente (primo accesso)
  async changeMyPassword(password: string): Promise<any> {
    const response = await this.api.put('/users/me/password', { password });
    return response.data;
  }

  // ============================================
  // PERMISSIONS API
  // ============================================

  // GET /api/users/me - Profilo utente corrente con permessi
  async getMyProfile(): Promise<any> {
    const response = await this.api.get('/users/me');
    return response.data;
  }

  // PUT /api/users/me - Aggiorna profilo utente corrente
  async updateMyProfile(data: { first_name: string; last_name: string; phone?: string; department?: string; preferences?: any }): Promise<any> {
    const response = await this.api.put('/users/me', data);
    return response.data;
  }

  // GET /api/users/me/permissions - Solo permessi utente corrente
  async getMyPermissions(): Promise<any> {
    const response = await this.api.get('/users/me/permissions');
    return response.data;
  }

  // GET /api/users/managers - Lista utenti che possono essere manager
  async getManagers(): Promise<any[]> {
    const response = await this.api.get('/users/managers');
    return response.data;
  }

  // GET /api/users/hierarchy - Gerarchia completa utenti
  async getUserHierarchy(): Promise<any> {
    const response = await this.api.get('/users/hierarchy');
    return response.data;
  }

  // GET /api/users/:id/subordinates - Subordinati di un utente
  async getUserSubordinates(userId: number, directOnly = false): Promise<any[]> {
    const response = await this.api.get(`/users/${userId}/subordinates`, {
      params: { direct_only: directOnly }
    });
    return response.data;
  }

  // GET /api/users/:id/team - Membri del team di un utente
  async getUserTeam(userId: number): Promise<any[]> {
    const response = await this.api.get(`/users/${userId}/team`);
    return response.data;
  }

  // ============================================
  // APPOINTMENTS API
  // ============================================

  // GET /api/appointments/statuses - Lista stati disponibili
  async getAppointmentStatuses(): Promise<any[]> {
    const response = await this.api.get('/appointments/statuses');
    return response.data;
  }

  // GET /api/appointments/meeting-types - Lista tipi meeting
  async getAppointmentMeetingTypes(): Promise<any[]> {
    const response = await this.api.get('/appointments/meeting-types');
    return response.data;
  }

  // GET /api/appointments/visible-users - Lista utenti visibili per filtro calendario
  async getAppointmentsVisibleUsers(): Promise<{
    users: Array<{
      id: number;
      firstName: string;
      lastName: string;
      fullName: string;
      role: string;
      email: string;
      color: string;
      isCurrentUser: boolean;
    }>;
    currentUserId: number;
    isAdmin: boolean;
    hasSubordinates: boolean;
  }> {
    const response = await this.api.get('/appointments/visible-users');
    return response.data;
  }

  // GET /api/appointments - Lista appuntamenti
  async getAppointments(params?: {
    search?: string;
    client_id?: number;
    assigned_to?: number;
    user_ids?: string;
    include_team?: string;
    status?: string;
    meeting_type?: string;
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const response = await this.api.get('/appointments', { params });
    return response.data;
  }

  // GET /api/appointments/:id - Dettaglio appuntamento
  async getAppointment(id: number): Promise<any> {
    const response = await this.api.get(`/appointments/${id}`);
    return response.data;
  }

  // GET /api/appointments/calendar - Vista calendario
  async getAppointmentsCalendar(params: {
    month: number;
    year: number;
    user_ids?: string;
    include_team?: string;
  }): Promise<{
    month: number;
    year: number;
    appointments: Record<string, any[]>;
    total: number;
    userLegend: Array<{
      userId: number;
      name: string;
      color: string;
      isCurrentUser: boolean;
    }>;
    currentUserId: number;
  }> {
    const response = await this.api.get('/appointments/calendar', { params });
    return response.data;
  }

  // GET /api/appointments/upcoming - Prossimi appuntamenti
  async getUpcomingAppointments(limit = 10): Promise<any[]> {
    const response = await this.api.get('/appointments/upcoming', { params: { limit } });
    return response.data;
  }

  // POST /api/appointments - Crea appuntamento
  async createAppointment(data: {
    title: string;
    description?: string;
    client_id: number;
    quote_id?: number;
    assigned_to?: number;
    scheduled_datetime: string;
    duration_minutes?: number;
    status?: string;
    location?: string;
    meeting_type?: string;
    notes?: string;
  }): Promise<any> {
    const response = await this.api.post('/appointments', data);
    return response.data;
  }

  // PUT /api/appointments/:id - Modifica appuntamento
  async updateAppointment(id: number, data: {
    title?: string;
    description?: string;
    client_id?: number;
    quote_id?: number | null;
    assigned_to?: number;
    scheduled_datetime?: string;
    duration_minutes?: number;
    status?: string;
    location?: string;
    meeting_type?: string;
    notes?: string;
  }): Promise<any> {
    const response = await this.api.put(`/appointments/${id}`, data);
    return response.data;
  }

  // PUT /api/appointments/:id/status - Cambia stato appuntamento
  async updateAppointmentStatus(id: number, status: string): Promise<any> {
    const response = await this.api.put(`/appointments/${id}/status`, { status });
    return response.data;
  }

  // DELETE /api/appointments/:id - Elimina appuntamento
  async deleteAppointment(id: number): Promise<any> {
    const response = await this.api.delete(`/appointments/${id}`);
    return response.data;
  }

  // ============================================
  // USER UNAVAILABILITY API
  // ============================================

  async getUnavailabilities(params?: {
    user_id?: number;
    date_from?: string;
    date_to?: string;
    reason?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const response = await this.api.get('/user-unavailability', { params });
    return response.data;
  }

  async getUnavailabilityCalendar(params: {
    month: number;
    year: number;
    user_ids?: string;
  }): Promise<{ unavailabilities: any[] }> {
    const response = await this.api.get('/user-unavailability/calendar', { params });
    return response.data;
  }

  async checkUserAvailability(params: {
    user_id: number;
    datetime: string;
    duration_minutes: number;
  }): Promise<{ available: boolean; conflicts: any[] }> {
    const response = await this.api.get('/user-unavailability/check', { params });
    return response.data;
  }

  async createUnavailability(data: {
    title: string;
    reason: string;
    start_datetime: string;
    end_datetime: string;
    all_day?: boolean;
    notes?: string;
    user_id?: number;
  }): Promise<any> {
    const response = await this.api.post('/user-unavailability', data);
    return response.data;
  }

  async updateUnavailability(id: number, data: {
    title?: string;
    reason?: string;
    start_datetime?: string;
    end_datetime?: string;
    all_day?: boolean;
    notes?: string;
  }): Promise<any> {
    const response = await this.api.put(`/user-unavailability/${id}`, data);
    return response.data;
  }

  async deleteUnavailability(id: number): Promise<any> {
    const response = await this.api.delete(`/user-unavailability/${id}`);
    return response.data;
  }

  // ============================================
  // OPPORTUNITIES API (Pipeline)
  // ============================================

  // GET /api/opportunities - Lista opportunita
  async getOpportunities(params?: {
    search?: string;
    client_id?: number;
    owner_id?: number;
    stage_id?: number;
    status?: string;
    priority?: string;
    date_from?: string;
    date_to?: string;
    min_value?: number;
    max_value?: number;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const response = await this.api.get('/opportunities', { params });
    return response.data;
  }

  // GET /api/opportunities/stats - Statistiche rapide
  async getOpportunitiesStats(): Promise<any> {
    const response = await this.api.get('/opportunities/stats');
    return response.data;
  }

  // GET /api/opportunities/:id - Dettaglio opportunita
  async getOpportunity(id: number): Promise<any> {
    const response = await this.api.get(`/opportunities/${id}`);
    return response.data;
  }

  // GET /api/opportunities/:id/quotes - Preventivi collegati
  async getOpportunityQuotes(id: number): Promise<any[]> {
    const response = await this.api.get(`/opportunities/${id}/quotes`);
    return response.data;
  }

  // GET /api/opportunities/:id/activities - Activity log
  async getOpportunityActivities(id: number, limit = 50): Promise<any[]> {
    const response = await this.api.get(`/opportunities/${id}/activities`, { params: { limit } });
    return response.data;
  }

  // POST /api/opportunities - Crea opportunita
  async createOpportunity(data: {
    name: string;
    description?: string;
    client_id?: number;
    owner_id?: number;
    stage_id: number;
    estimated_value?: number;
    currency?: string;
    expected_close_date?: string;
    probability?: number;
    status?: string;
    priority?: string;
    source?: string;
    competitor?: string;
  }): Promise<any> {
    const response = await this.api.post('/opportunities', data);
    return response.data;
  }

  // PUT /api/opportunities/:id - Modifica opportunita
  async updateOpportunity(id: number, data: {
    name?: string;
    description?: string;
    client_id?: number;
    owner_id?: number;
    stage_id?: number;
    estimated_value?: number;
    currency?: string;
    expected_close_date?: string;
    probability?: number;
    priority?: string;
    source?: string;
    competitor?: string;
  }): Promise<any> {
    const response = await this.api.put(`/opportunities/${id}`, data);
    return response.data;
  }

  // PATCH /api/opportunities/:id/stage - Cambia stadio (drag & drop)
  async changeOpportunityStage(id: number, stage_id: number): Promise<any> {
    const response = await this.api.patch(`/opportunities/${id}/stage`, { stage_id });
    return response.data;
  }

  // POST /api/opportunities/:id/close - Chiudi opportunita
  async closeOpportunity(id: number, data: {
    status: 'won' | 'lost';
    win_loss_reason_id: number;
    win_loss_notes?: string;
    competitor?: string;
  }): Promise<any> {
    const response = await this.api.post(`/opportunities/${id}/close`, data);
    return response.data;
  }

  // POST /api/opportunities/:id/reopen - Riapri opportunita
  async reopenOpportunity(id: number, stage_id?: number): Promise<any> {
    const response = await this.api.post(`/opportunities/${id}/reopen`, { stage_id });
    return response.data;
  }

  // DELETE /api/opportunities/:id - Archivia/elimina opportunita
  async deleteOpportunity(id: number, permanent = false): Promise<any> {
    const response = await this.api.delete(`/opportunities/${id}`, { params: { permanent } });
    return response.data;
  }

  // ============================================
  // PIPELINE API (Stages, Kanban, Forecast)
  // ============================================

  // GET /api/pipeline/stages - Lista stadi pipeline
  async getPipelineStages(includeInactive = false): Promise<any[]> {
    const response = await this.api.get('/pipeline/stages', { params: { include_inactive: includeInactive } });
    return response.data;
  }

  // GET /api/pipeline/stages/:id - Dettaglio stadio
  async getPipelineStage(id: number): Promise<any> {
    const response = await this.api.get(`/pipeline/stages/${id}`);
    return response.data;
  }

  // POST /api/pipeline/stages - Crea stadio (admin)
  async createPipelineStage(data: {
    name: string;
    code: string;
    probability?: number;
    color?: string;
    is_closed?: boolean;
    is_won?: boolean;
  }): Promise<any> {
    const response = await this.api.post('/pipeline/stages', data);
    return response.data;
  }

  // PUT /api/pipeline/stages/:id - Modifica stadio (admin)
  async updatePipelineStage(id: number, data: {
    name?: string;
    probability?: number;
    color?: string;
    is_closed?: boolean;
    is_won?: boolean;
    is_active?: boolean;
  }): Promise<any> {
    const response = await this.api.put(`/pipeline/stages/${id}`, data);
    return response.data;
  }

  // PUT /api/pipeline/stages/reorder - Riordina stadi (admin)
  async reorderPipelineStages(stages: Array<{ id: number; position: number }>): Promise<any[]> {
    const response = await this.api.put('/pipeline/stages/reorder', { stages });
    return response.data;
  }

  // GET /api/pipeline/kanban - Dati per vista Kanban
  async getPipelineKanban(params?: {
    owner_id?: number;
    client_id?: number;
    priority?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<{
    stages: Array<{
      stage: any;
      opportunities: any[];
      count: number;
      totalValue: number;
      weightedValue: number;
    }>;
    totals: {
      count: number;
      totalValue: number;
      weightedValue: number;
    };
  }> {
    const response = await this.api.get('/pipeline/kanban', { params });
    return response.data;
  }

  // GET /api/pipeline/forecast - Dati forecast
  async getPipelineForecast(params?: {
    owner_id?: number;
    period?: 'month' | 'quarter' | 'year';
  }): Promise<any> {
    const response = await this.api.get('/pipeline/forecast', { params });
    return response.data;
  }

  // GET /api/pipeline/analytics - Metriche pipeline
  async getPipelineAnalytics(params?: {
    owner_id?: number;
    date_from?: string;
    date_to?: string;
  }): Promise<any> {
    const response = await this.api.get('/pipeline/analytics', { params });
    return response.data;
  }

  // ============================================
  // WIN/LOSS REASONS API
  // ============================================

  // GET /api/win-loss-reasons - Lista motivi
  async getWinLossReasons(type?: 'win' | 'loss', includeInactive = false): Promise<{
    all: any[];
    grouped: { win: any[]; loss: any[] };
  }> {
    const response = await this.api.get('/win-loss-reasons', {
      params: { type, include_inactive: includeInactive }
    });
    return response.data;
  }

  // GET /api/win-loss-reasons/:id - Dettaglio motivo
  async getWinLossReason(id: number): Promise<any> {
    const response = await this.api.get(`/win-loss-reasons/${id}`);
    return response.data;
  }

  // POST /api/win-loss-reasons - Crea motivo (admin)
  async createWinLossReason(data: {
    type: 'win' | 'loss';
    name: string;
    description?: string;
  }): Promise<any> {
    const response = await this.api.post('/win-loss-reasons', data);
    return response.data;
  }

  // PUT /api/win-loss-reasons/:id - Modifica motivo (admin)
  async updateWinLossReason(id: number, data: {
    name?: string;
    description?: string;
    position?: number;
    is_active?: boolean;
  }): Promise<any> {
    const response = await this.api.put(`/win-loss-reasons/${id}`, data);
    return response.data;
  }

  // DELETE /api/win-loss-reasons/:id - Disattiva motivo (admin)
  async deleteWinLossReason(id: number): Promise<any> {
    const response = await this.api.delete(`/win-loss-reasons/${id}`);
    return response.data;
  }

  // GET /api/win-loss-reasons/analytics/usage - Statistiche uso motivi
  async getWinLossAnalytics(params?: {
    date_from?: string;
    date_to?: string;
  }): Promise<any> {
    const response = await this.api.get('/win-loss-reasons/analytics/usage', { params });
    return response.data;
  }

  // ============================================
  // EMAIL TEMPLATES API
  // ============================================

  // GET /api/email-templates - Lista template email
  async getEmailTemplates(params?: {
    type?: string;
    is_active?: boolean;
  }): Promise<any[]> {
    const response = await this.api.get('/email-templates', { params });
    return response.data;
  }

  // GET /api/email-templates/default/:type - Template default per tipo
  async getDefaultEmailTemplate(type: string): Promise<any> {
    const response = await this.api.get(`/email-templates/default/${type}`);
    return response.data;
  }

  // GET /api/email-templates/:id - Dettaglio template
  async getEmailTemplate(id: number): Promise<any> {
    const response = await this.api.get(`/email-templates/${id}`);
    return response.data;
  }

  // POST /api/email-templates - Crea template
  async createEmailTemplate(data: {
    name: string;
    type: string;
    subject: string;
    body_html: string;
    body_text?: string;
    variables?: string[];
    is_default?: boolean;
    is_active?: boolean;
  }): Promise<any> {
    const response = await this.api.post('/email-templates', data);
    return response.data;
  }

  // PUT /api/email-templates/:id - Modifica template
  async updateEmailTemplate(id: number, data: {
    name?: string;
    type?: string;
    subject?: string;
    body_html?: string;
    body_text?: string;
    variables?: string[];
    is_default?: boolean;
    is_active?: boolean;
  }): Promise<any> {
    const response = await this.api.put(`/email-templates/${id}`, data);
    return response.data;
  }

  // DELETE /api/email-templates/:id - Elimina template
  async deleteEmailTemplate(id: number): Promise<any> {
    const response = await this.api.delete(`/email-templates/${id}`);
    return response.data;
  }

  // POST /api/email-templates/:id/preview - Preview template con dati
  async previewEmailTemplate(id: number, quoteId?: number): Promise<{
    subject: string;
    body_html: string;
    body_text: string;
  }> {
    const response = await this.api.post(`/email-templates/${id}/preview`, { quote_id: quoteId });
    return response.data;
  }

  // ============================================
  // QUOTE EMAILS API (Invio e tracking)
  // ============================================

  // GET /api/quote-emails/quote/:quoteId - Storico email per preventivo
  async getQuoteEmails(quoteId: number): Promise<any[]> {
    const response = await this.api.get(`/quote-emails/quote/${quoteId}`);
    return response.data;
  }

  // GET /api/quote-emails/:id - Dettaglio email
  async getQuoteEmail(id: number): Promise<any> {
    const response = await this.api.get(`/quote-emails/${id}`);
    return response.data;
  }

  // GET /api/quote-emails/stats/:quoteId - Statistiche email preventivo
  async getQuoteEmailStats(quoteId: number): Promise<{
    total_sent: number;
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
    failed: number;
    last_sent_at: string | null;
    last_opened_at: string | null;
    last_clicked_at: string | null;
  }> {
    const response = await this.api.get(`/quote-emails/stats/${quoteId}`);
    return response.data;
  }

  // POST /api/quote-emails/send - Invia email con preventivo
  async sendQuoteEmail(data: {
    quote_id: number;
    template_id?: number;
    sender_email?: string;
    recipient_email: string;
    cc_emails?: string[];
    bcc_emails?: string[];
    subject: string;
    body_html: string;
    body_text?: string;
    attachment_type?: 'pdf' | 'link' | 'both';
    custom_message?: string;
  }): Promise<{
    success: boolean;
    message: string;
    email_id: number;
    tracking_token: string;
    view_link: string;
  }> {
    const response = await this.api.post('/quote-emails/send', data);
    return response.data;
  }

  // POST /api/quote-emails/:quoteId/send-reminder - Invio manuale reminder
  async sendQuoteReminder(quoteId: number): Promise<{ success: boolean; message: string; email_id: number }> {
    const response = await this.api.post(`/quote-emails/${quoteId}/send-reminder`);
    return response.data;
  }

  // POST /api/quote-emails/:quoteId/send-followup - Invio manuale follow-up
  async sendQuoteFollowUp(quoteId: number): Promise<{ success: boolean; message: string; email_id: number }> {
    const response = await this.api.post(`/quote-emails/${quoteId}/send-followup`);
    return response.data;
  }

  // ============================================
  // NOTIFICATIONS API
  // ============================================

  // GET /api/notifications - Lista notifiche utente
  async getNotifications(params?: {
    unread_only?: boolean;
    type?: string;
    limit?: number;
    offset?: number;
  }): Promise<any[]> {
    const response = await this.api.get('/notifications', { params });
    return response.data;
  }

  // GET /api/notifications/count - Conteggio non lette
  async getNotificationsCount(): Promise<{ unread_count: number }> {
    const response = await this.api.get('/notifications/count');
    return response.data;
  }

  // GET /api/notifications/grouped - Notifiche raggruppate per tipo
  async getNotificationsGrouped(): Promise<Array<{
    type: string;
    count: number;
    unread_count: number;
    latest_at: string;
  }>> {
    const response = await this.api.get('/notifications/grouped');
    return response.data;
  }

  // PUT /api/notifications/:id/read - Segna come letta
  async markNotificationRead(id: number): Promise<any> {
    const response = await this.api.put(`/notifications/${id}/read`);
    return response.data;
  }

  // PUT /api/notifications/read-all - Segna tutte come lette
  async markAllNotificationsRead(type?: string): Promise<{ marked_read: number }> {
    const response = await this.api.put('/notifications/read-all', { type });
    return response.data;
  }

  // PUT /api/notifications/:id/dismiss - Elimina notifica
  async dismissNotification(id: number): Promise<any> {
    const response = await this.api.put(`/notifications/${id}/dismiss`);
    return response.data;
  }

  // DELETE /api/notifications/clear-all - Elimina tutte
  async clearAllNotifications(olderThanDays?: number): Promise<{ success: boolean; cleared: number }> {
    const response = await this.api.delete('/notifications/clear-all', {
      params: { older_than_days: olderThanDays }
    });
    return response.data;
  }

  // ============================================
  // CLIENT EMAILS API (Storico comunicazioni)
  // ============================================

  // GET /api/quote-emails/client/:clientId - Storico email per cliente
  async getClientEmails(clientId: number, params?: {
    limit?: number;
    offset?: number;
  }): Promise<{
    data: any[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const response = await this.api.get(`/quote-emails/client/${clientId}`, { params });
    return response.data;
  }

  // GET /api/quote-emails/client/:clientId/stats - Statistiche email cliente
  async getClientEmailStats(clientId: number): Promise<{
    total_sent: number;
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
    failed: number;
    last_sent_at: string | null;
    last_opened_at: string | null;
    quotes_contacted: number;
  }> {
    const response = await this.api.get(`/quote-emails/client/${clientId}/stats`);
    return response.data;
  }

  // ============================================
  // ORDER CONFIRMATIONS API (Conferme d'Ordine)
  // ============================================

  // GET /api/order-confirmations - Lista conferme d'ordine
  async getOrderConfirmations(params?: {
    search?: string;
    client_id?: number;
    status?: string;
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const response = await this.api.get('/order-confirmations', { params });
    return response.data;
  }

  // GET /api/order-confirmations/:id - Dettaglio conferma d'ordine
  async getOrderConfirmation(id: number): Promise<any> {
    const response = await this.api.get(`/order-confirmations/${id}`);
    return response.data;
  }

  // GET /api/order-confirmations/by-quote/:quoteId - Conferma d'ordine per preventivo
  async getOrderConfirmationByQuote(quoteId: number): Promise<any | null> {
    const response = await this.api.get(`/order-confirmations/by-quote/${quoteId}`);
    return response.data;
  }

  // POST /api/order-confirmations - Crea conferma d'ordine da preventivo accettato
  async createOrderConfirmation(quoteId: number): Promise<any> {
    const response = await this.api.post('/order-confirmations', { quote_id: quoteId });
    return response.data;
  }

  // PUT /api/order-confirmations/:id - Aggiorna stato conferma d'ordine
  async updateOrderConfirmationStatus(id: number, status: string): Promise<any> {
    const response = await this.api.put(`/order-confirmations/${id}`, { status });
    return response.data;
  }

  // PUT /api/order-confirmations/:id - Aggiorna campi conferma d'ordine
  async updateOrderConfirmation(id: number, data: { delivery_date?: string | null; notes?: string; status?: string }): Promise<any> {
    const response = await this.api.put(`/order-confirmations/${id}`, data);
    return response.data;
  }

  // DELETE /api/order-confirmations/:id - Elimina conferma d'ordine
  async deleteOrderConfirmation(id: number): Promise<any> {
    const response = await this.api.delete(`/order-confirmations/${id}`);
    return response.data;
  }

  // GET /api/order-confirmations/:id/pdf - Download PDF conferma d'ordine
  async downloadOrderConfirmationPdf(id: number, confirmationNumber: string): Promise<void> {
    const response = await this.api.get(`/order-confirmations/${id}/pdf`, {
      responseType: 'blob'
    });

    // Crea download automatico
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `conferma-ordine-${confirmationNumber}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // Ottieni URL per aprire PDF in nuova tab
  getOrderConfirmationPdfUrl(id: number): string {
    return `${this.api.defaults.baseURL}/order-confirmations/${id}/pdf`;
  }

  // POST /api/order-confirmations/:id/send-email - Invia conferma d'ordine via email
  async sendOrderConfirmationEmail(id: number, recipientEmail?: string): Promise<any> {
    const response = await this.api.post(`/order-confirmations/${id}/send-email`, {
      recipient_email: recipientEmail,
    });
    return response.data;
  }

  // ============================================
  // SALESPEOPLE API (Team Commerciale)
  // ============================================

  // GET /api/salespeople - Lista commerciali
  async getSalespeople(params?: {
    search?: string;
    levelId?: number;
    isActive?: string;
  }): Promise<{
    data: any[];
    stats: {
      total: number;
      active: number;
      levels_used: number;
    };
  }> {
    const response = await this.api.get('/salespeople', { params });
    return response.data;
  }

  // GET /api/salespeople/:id - Dettaglio commerciale
  async getSalesperson(id: number): Promise<any> {
    const response = await this.api.get(`/salespeople/${id}`);
    return response.data;
  }

  // POST /api/salespeople - Crea commerciale
  async createSalesperson(data: {
    userId?: number;
    name: string;
    email?: string;
    phone?: string;
    levelId?: number;
    customMaxDiscount?: number;
    quotaMonthly?: number;
    notes?: string;
  }): Promise<any> {
    const response = await this.api.post('/salespeople', data);
    return response.data;
  }

  // PUT /api/salespeople/:id - Aggiorna commerciale
  async updateSalesperson(id: number, data: {
    userId?: number;
    name?: string;
    email?: string;
    phone?: string;
    levelId?: number;
    customMaxDiscount?: number;
    quotaMonthly?: number;
    notes?: string;
    isActive?: boolean;
  }): Promise<any> {
    const response = await this.api.put(`/salespeople/${id}`, data);
    return response.data;
  }

  // DELETE /api/salespeople/:id - Disattiva commerciale
  async deleteSalesperson(id: number): Promise<any> {
    const response = await this.api.delete(`/salespeople/${id}`);
    return response.data;
  }

  // GET /api/salespeople/:id/stats - Statistiche commerciale
  async getSalespersonStats(id: number): Promise<{
    quotesCount: number;
    quotesTotal: number;
    quotesAccepted: number;
    avgDiscount: string;
  }> {
    const response = await this.api.get(`/salespeople/${id}/stats`);
    return response.data;
  }

  // GET /api/salespeople/available-users - Utenti disponibili per collegamento
  async getAvailableUsersForSalesperson(excludeSalespersonId?: number): Promise<{
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    role: string;
  }[]> {
    const response = await this.api.get('/salespeople/available-users', {
      params: excludeSalespersonId ? { excludeSalespersonId } : undefined
    });
    return response.data;
  }

  // ============================================
  // SALES LEVELS API (Livelli Commerciali)
  // ============================================

  // GET /api/sales-levels - Lista livelli
  async getSalesLevels(isActive?: string): Promise<any[]> {
    const response = await this.api.get('/sales-levels', { params: { isActive } });
    return response.data;
  }

  // GET /api/sales-levels/:id - Dettaglio livello
  async getSalesLevel(id: number): Promise<any> {
    const response = await this.api.get(`/sales-levels/${id}`);
    return response.data;
  }

  // POST /api/sales-levels - Crea livello
  async createSalesLevel(data: {
    name: string;
    code: string;
    maxDiscountPercent: number;
    canApproveOthers?: boolean;
    description?: string;
    displayOrder?: number;
  }): Promise<any> {
    const response = await this.api.post('/sales-levels', data);
    return response.data;
  }

  // PUT /api/sales-levels/:id - Aggiorna livello
  async updateSalesLevel(id: number, data: {
    name?: string;
    code?: string;
    maxDiscountPercent?: number;
    canApproveOthers?: boolean;
    description?: string;
    displayOrder?: number;
    isActive?: boolean;
  }): Promise<any> {
    const response = await this.api.put(`/sales-levels/${id}`, data);
    return response.data;
  }

  // DELETE /api/sales-levels/:id - Disattiva livello
  async deleteSalesLevel(id: number): Promise<any> {
    const response = await this.api.delete(`/sales-levels/${id}`);
    return response.data;
  }

  // POST /api/sales-levels/reorder - Riordina livelli
  async reorderSalesLevels(order: Array<{ id: number; displayOrder: number }>): Promise<any> {
    const response = await this.api.post('/sales-levels/reorder', { order });
    return response.data;
  }

  // ============================================
  // APPROVAL RULES API (Regole Approvazione)
  // ============================================

  // GET /api/approval-rules - Lista regole
  async getApprovalRules(params?: {
    ruleType?: string;
    isActive?: string;
  }): Promise<any[]> {
    const response = await this.api.get('/approval-rules', { params });
    return response.data;
  }

  // GET /api/approval-rules/types - Tipi regole disponibili
  async getApprovalRuleTypes(): Promise<{
    ruleTypes: Array<{ value: string; label: string; description: string }>;
    conditionOperators: Array<{ value: string; label: string; symbol: string }>;
  }> {
    const response = await this.api.get('/approval-rules/types');
    return response.data;
  }

  // GET /api/approval-rules/:id - Dettaglio regola
  async getApprovalRule(id: number): Promise<any> {
    const response = await this.api.get(`/approval-rules/${id}`);
    return response.data;
  }

  // POST /api/approval-rules - Crea regola
  async createApprovalRule(data: {
    name: string;
    ruleType: string;
    conditionOperator: string;
    conditionValue: number;
    approverLevelId?: number;
    approverUserId?: number;
    priority?: number;
  }): Promise<any> {
    const response = await this.api.post('/approval-rules', data);
    return response.data;
  }

  // PUT /api/approval-rules/:id - Aggiorna regola
  async updateApprovalRule(id: number, data: {
    name?: string;
    ruleType?: string;
    conditionOperator?: string;
    conditionValue?: number;
    approverLevelId?: number;
    approverUserId?: number;
    priority?: number;
    isActive?: boolean;
  }): Promise<any> {
    const response = await this.api.put(`/approval-rules/${id}`, data);
    return response.data;
  }

  // DELETE /api/approval-rules/:id - Disattiva regola
  async deleteApprovalRule(id: number): Promise<any> {
    const response = await this.api.delete(`/approval-rules/${id}`);
    return response.data;
  }

  // POST /api/approval-rules/check - Verifica se richiede approvazione
  async checkApprovalRequired(data: {
    discountPercent?: number;
    quoteAmount?: number;
    productCategoryId?: number;
    salespersonId?: number;
  }): Promise<{
    requiresApproval: boolean;
    exceedsPersonalLimit: boolean;
    maxAllowedDiscount: number;
    requestedDiscount?: number;
    triggeredRules: Array<{
      id: number;
      name: string;
      ruleType: string;
      conditionOperator: string;
      conditionValue: number;
      approverLevel?: string;
      approverName?: string;
      approverEmail?: string;
    }>;
  }> {
    const response = await this.api.post('/approval-rules/check', data);
    return response.data;
  }

  // ============================================
  // DISCOUNT MATRIX API
  // ============================================

  // GET /api/discount-matrix - Lista matrice sconti
  async getDiscountMatrix(): Promise<any[]> {
    const response = await this.api.get('/discount-matrix');
    return response.data;
  }

  // GET /api/discount-matrix/check - Verifica sconto max per ruolo e tipo cliente
  async checkDiscountMatrix(params: {
    role: string;
    customerType: string;
  }): Promise<{
    maxDiscountPercent: number;
    requiresApproval: boolean;
    approvalRole: string | null;
    found: boolean;
  }> {
    const response = await this.api.get('/discount-matrix/check', { params });
    return response.data;
  }

  // GET /api/discount-matrix/user-limit - Limite sconto per utente corrente e cliente
  async getUserDiscountLimit(clientId?: number): Promise<{
    maxDiscountPercent: number;
    customerType: string;
    userRole: string;
    requiresApproval: boolean;
    approvalRole: string | null;
    source: 'matrix' | 'default';
  }> {
    const response = await this.api.get('/discount-matrix/user-limit', {
      params: clientId ? { clientId } : {}
    });
    return response.data;
  }

  // POST /api/discount-matrix - Crea entry matrice
  async createDiscountMatrixEntry(data: {
    role: string;
    customerType: string;
    maxDiscountPercent: number;
    requiresApproval?: boolean;
    approvalRole?: string;
    notes?: string;
  }): Promise<any> {
    const response = await this.api.post('/discount-matrix', data);
    return response.data;
  }

  // PUT /api/discount-matrix/:id - Aggiorna entry matrice
  async updateDiscountMatrixEntry(id: number, data: {
    maxDiscountPercent?: number;
    requiresApproval?: boolean;
    approvalRole?: string;
    notes?: string;
    isActive?: boolean;
  }): Promise<any> {
    const response = await this.api.put(`/discount-matrix/${id}`, data);
    return response.data;
  }

  // DELETE /api/discount-matrix/:id - Elimina entry matrice
  async deleteDiscountMatrixEntry(id: number): Promise<any> {
    const response = await this.api.delete(`/discount-matrix/${id}`);
    return response.data;
  }

  // POST /api/discount-matrix/bulk-update - Aggiornamento massivo matrice
  async bulkUpdateDiscountMatrix(entries: Array<{
    role: string;
    customerType: string;
    maxDiscountPercent: number;
    requiresApproval?: boolean;
    approvalRole?: string;
  }>): Promise<{ updated: number; entries: any[] }> {
    const response = await this.api.post('/discount-matrix/bulk-update', { entries });
    return response.data;
  }

  // ============================================
  // ADMIN DASHBOARD (Platform Admin)
  // ============================================

  async getAdminDashboard(): Promise<any> {
    const response = await this.api.get('/admin-dashboard');
    return response.data;
  }

  // ============================================
  // ADMIN USERS (Cross-tenant user management)
  // ============================================

  async getAdminUsers(params?: {
    search?: string;
    role?: string;
    is_active?: string;
    tenant_id?: number;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const response = await this.api.get('/admin-users', { params });
    return response.data;
  }

  async getAdminUserLockoutStatus(userId: number): Promise<{
    isLocked: boolean;
    failedAttempts: number;
    lockedUntil: string | null;
    lastFailedLogin: string | null;
  }> {
    const response = await this.api.get(`/admin-users/${userId}/lockout-status`);
    return response.data;
  }

  async adminUnlockUser(userId: number): Promise<{
    message: string;
    email: string;
    wasLocked: boolean;
    previousAttempts: number;
  }> {
    const response = await this.api.post(`/admin-users/${userId}/unlock`);
    return response.data;
  }

  // ============================================
  // EMAIL TEST MODE
  // ============================================

  async getEmailTestStatus(): Promise<{
    active: boolean;
    quote: { quote_number: string; client_email: string } | null;
    emailsSent: number;
    logs: Array<{ timestamp: string; type: 'info' | 'success' | 'error'; message: string }>;
  }> {
    const response = await this.api.get('/email-test/status');
    return response.data;
  }

  async startEmailTest(): Promise<{
    success: boolean;
    message: string;
    quote?: { quote_number: string; client_email: string };
  }> {
    const response = await this.api.post('/email-test/start');
    return response.data;
  }

  async stopEmailTest(): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post('/email-test/stop');
    return response.data;
  }

  // ============================================
  // AUDIT LOGS
  // ============================================

  async getAuditLogs(params?: {
    page?: number;
    limit?: number;
    category?: string;
    action?: string;
    severity?: string;
    entity_type?: string;
    user_id?: number;
    date_from?: string;
    date_to?: string;
    search?: string;
    tenant_id?: number;
  }): Promise<{ data: any[]; total: number; page: number; limit: number }> {
    const response = await this.api.get('/audit-logs', { params });
    return response.data;
  }

  async getAuditLogDetail(id: number): Promise<any> {
    const response = await this.api.get(`/audit-logs/${id}`);
    return response.data;
  }

  async getAuditLogStats(params?: {
    date_from?: string;
    date_to?: string;
    tenant_id?: number;
  }): Promise<any> {
    const response = await this.api.get('/audit-logs/stats', { params });
    return response.data;
  }

  async exportAuditLogs(params?: any): Promise<Blob> {
    const response = await this.api.get('/audit-logs/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  async getEntityAuditHistory(entityType: string, entityId: number): Promise<any[]> {
    const response = await this.api.get(`/audit-logs/entity/${entityType}/${entityId}`);
    return response.data;
  }
}

export default new ApiService();